import { useCallback, useEffect, useMemo, useState } from 'react';

import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

import {
  useGetDatabaseSchemaQuery,
  useGetReportingColumnsQuery,
  useSaveReportingColumnsMutation,
  useValidateReportsMutation,
} from 'apis/report-configuration.api';
import { useOpen } from 'hooks/useOpen';
import { DatabaseColumn } from 'interfaces/report-configuration.interface';
import { CommonError } from 'interfaces/shared.interface';
import AddTablesModal from 'page-components/report-configuration/AddTablesModal';
import AvailableTableItemBlock from 'page-components/report-configuration/AvailableTableItemBlock';
import AvailableTables from 'page-components/report-configuration/AvailableTables';
import WarningList from 'page-components/report-configuration/WarningList';
import { Button, ErrorModal, LoadingOverlay, WarningModal } from 'shared-components';
import { successNotify } from 'utils/notifications';

const ReportConfiguration = () => {
  const [isOpenWarningModal, handleOnOpenWarningModal, handleCloseWarningModal] = useOpen();
  const [isErrorModalOpen, handleOpenErrorModal, handleCloseErrorModal] = useOpen();
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [selectedReportColumns, setSelectedReportColumns] = useState<DatabaseColumn[]>([]);
  const [validateReports] = useValidateReportsMutation();
  const [saveReportingColumns, { error: saveReportingColumnsError }] = useSaveReportingColumnsMutation();
  const [isOpenAddTablesModal, handleOpenAddTablesModal, handleCloseAddTablesModal] = useOpen();
  const [warnings, setWarnings] = useState<any[]>([]);
  const [validationPayload, setValidationPayload] = useState<any[]>([]);
  const [selectedTablesOrder, setSelectedTablesOrder] = useState<string[]>([]);

  const errorData = (saveReportingColumnsError as CommonError)?.data || {};

  const { data: schemaData, isLoading: isSchemaLoading } = useGetDatabaseSchemaQuery();
  const {
    data: selectedColumnsData,
    isLoading: isSelectedColumnsLoading,
    refetch: refetchSelectedColumns,
  } = useGetReportingColumnsQuery();

  const { t } = useTranslation();

  const tablesData = useMemo(() => {
    if (!schemaData) {
      return [];
    }

    return selectedTablesOrder
      .filter((tableName) => tableName !== 'Id')
      .map((tableName) => {
        const schemaTable = schemaData.result.find((table) => table.name === tableName);

        const selectedCount = selectedReportColumns.filter(
          (col) => col.tableName === tableName && col.name !== ''
        ).length;

        const totalColumns = schemaTable ? schemaTable.columns.length : 0;

        return {
          name: tableName,
          tableName,
          selectedColumns: selectedCount,
          totalColumns,
        };
      });
  }, [schemaData, selectedReportColumns, selectedTablesOrder]);

  const handleSelect = (tableName: string) => {
    setSelectedTable(tableName);
  };

  const handleToggleColumn = useCallback((column: DatabaseColumn) => {
    setSelectedReportColumns((prev) => {
      const isSelected = prev.some((col) => col.name === column.name && col.tableName === column.tableName);

      if (isSelected) {
        return prev.filter((col) => !(col.name === column.name && col.tableName === column.tableName));
      } else {
        return [...prev, column];
      }
    });
  }, []);

  const handleConfirmSave = useCallback(
    async (payload: any) => {
      try {
        const saveResponse = await saveReportingColumns({ columns: payload }).unwrap();

        if (saveResponse.success) {
          setSelectedTable(null);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [saveReportingColumns]
  );

  const handleSave = useCallback(async () => {
    const payload = selectedReportColumns
      .filter((col) => col.name !== '')
      .map(({ name, tableName, type }) => ({
        name,
        tableName,
        columnType: type,
      }));

    try {
      const validateResponse = await validateReports({ columns: payload }).unwrap();

      if (!validateResponse.success) {
        return;
      }

      if (validateResponse.result?.warnings && validateResponse.result.warnings.length > 0) {
        setWarnings(validateResponse.result.warnings);
        setValidationPayload(payload);
        handleOnOpenWarningModal();
        return;
      }

      await handleConfirmSave(payload);
      successNotify(t('SavedSuccessfully'));
    } catch (error) {
      console.error(error);
    }
  }, [selectedReportColumns, selectedTable, validateReports]);

  const handleValidationTable = async () => {
    try {
      const saveResponse = await saveReportingColumns({ columns: validationPayload }).unwrap();

      if (saveResponse.success) {
        setSelectedTable(null);
      }
    } catch (error) {
      handleCloseWarningModal();
      handleOpenErrorModal();
    }
  };

  const handleCancel = useCallback(() => {
    refetchSelectedColumns()
      .unwrap()
      .then((response) => {
        const mappedColumns: DatabaseColumn[] = response.result.map((col) => ({
          name: col.name,
          tableName: col.tableName,
          type: col.columnType,
          displayType: '',
        }));
        setSelectedReportColumns(mappedColumns);
        setSelectedTable(null);
      })
      .catch((error) => {
        console.error(error);
        setSelectedTable(null);
      });
  }, [refetchSelectedColumns]);

  const selectedTableColumns = useMemo(() => {
    if (!selectedTable || !schemaData?.result) {
      return [];
    }
    return schemaData.result.find((table) => table.name === selectedTable)?.columns || [];
  }, [selectedTable, schemaData?.result]);

  const handleAddTablesCallback = useCallback(
    (selectedTables: { name: string }[]) => {
      try {
        const newTableNames = selectedTables.map((table) => table.name);

        const placeholderColumns: DatabaseColumn[] = [];

        newTableNames.forEach((tableName) => {
          if (!selectedReportColumns.some((col) => col.tableName === tableName)) {
            placeholderColumns.push({
              name: '',
              tableName,
              type: 0,
              displayType: '',
            });
          }
        });

        const combinedColumns = [...selectedReportColumns, ...placeholderColumns];

        setSelectedReportColumns(combinedColumns);

        setSelectedTablesOrder((prev) => [...prev, ...newTableNames.filter((name) => !prev.includes(name))]);
        handleCloseAddTablesModal();
        successNotify(t('SavedSuccessfully'));
      } catch (error) {
        handleOpenErrorModal();
      }
    },
    [selectedReportColumns, selectedTable]
  );

  useEffect(() => {
    if (!selectedColumnsData?.result || selectedReportColumns.length > 0) {
      return;
    }

    const mappedColumns: DatabaseColumn[] = selectedColumnsData.result.map((col) => ({
      name: col.name,
      tableName: col.tableName,
      type: col.columnType,
      displayType: '',
    }));

    setSelectedReportColumns(mappedColumns);

    const initialOrder = Array.from(new Set(mappedColumns.map((col) => col.tableName).filter((name) => name !== 'Id')));
    setSelectedTablesOrder(initialOrder);
  }, [selectedColumnsData, selectedReportColumns.length]);

  useEffect(() => {
    if (!schemaData?.result || selectedReportColumns.length === 0) {
      return;
    }

    const updatedSelectedTables = selectedReportColumns.filter((col) => {
      return schemaData.result.some((t) => t.name === col.tableName);
    });

    if (updatedSelectedTables.length !== selectedReportColumns.length) {
      setSelectedReportColumns(updatedSelectedTables);
    }
  }, [schemaData, selectedReportColumns]);

  return (
    <>
      <ErrorModal
        errorMessage={errorData?.error?.message}
        description={errorData?.error?.details}
        isOpen={isErrorModalOpen}
        handleClose={handleCloseErrorModal}
      />
      <WarningModal
        warningMessage={t('ConfirmChanges')}
        description={<WarningList warnings={warnings} />}
        isOpen={isOpenWarningModal}
        handleClose={handleCloseWarningModal}
        handleConfirm={handleValidationTable}
      />
      <AddTablesModal
        isOpen={isOpenAddTablesModal}
        handleClose={handleCloseAddTablesModal}
        selectedTables={Array.from(new Set(selectedReportColumns.map((col) => col.tableName))).map((name) => ({
          name,
        }))}
        onAddTablesConfirm={handleAddTablesCallback}
      />
      <Button
        dataCy="open-tenant-modal-button"
        className="mb-6"
        type="button"
        onClick={handleOpenAddTablesModal}
        leftIcon={faPlus}
        iconClassName="h-4"
      >
        {t('AddTables')}
      </Button>
      <div data-cy="report-configuration-page" className="md:flex gap-5">
        <LoadingOverlay isLoading={isSchemaLoading || isSelectedColumnsLoading} />
        <AvailableTables tables={tablesData} onSelect={handleSelect} selectedTable={selectedTable} />
        {selectedTable && (
          <AvailableTableItemBlock
            tableName={selectedTable}
            columns={selectedTableColumns}
            selectedColumns={selectedReportColumns.filter((col) => col.tableName === selectedTable)}
            onToggleColumn={handleToggleColumn}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}
      </div>
    </>
  );
};

export default ReportConfiguration;
